import AuthService from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export interface PluginEnvVar {
	key: string;
	label: string;
	type: string;
	default: string;
	shared?: boolean;
	required?: boolean;
	secret?: boolean;
}

export interface PluginUserVariable {
	key: string;
	label?: string;
	description?: string;
	group?: string;
	type?: string;
	required?: boolean;
	secret?: boolean;
	default?: string;
	valueHelp?: {
		path: string;
		title?: string;
		/** Allow picking several entries; stored comma separated. */
		multi?: boolean;
	};
}

export interface PluginPage {
	name: string;
	icon: string;
	view: string;
	/**
	 * Optional side-navigation grouping label. Consecutive pages naming the same
	 * section are drawn under one label; a page without one stands alone. Like
	 * `name`, it may be an `{i18n>key}` reference into the plugin's own bundle.
	 */
	section?: string;
	/**
	 * Full portal role keys that may SEE this page in the side navigation.
	 *
	 * Absent means everyone who can reach the plugin, which is what every page
	 * meant before this field existed - so no manifest changes meaning by being
	 * left alone. `system:admin` always qualifies.
	 *
	 * This hides an entry; it does not protect the page. A plugin's own routes
	 * are what refuse the data, and they already do - a user who types the URL
	 * of a page they cannot see gets the view and an empty, refusing screen.
	 * Declaring roles here without guarding the routes would be a lock on a door
	 * in an open wall.
	 */
	roles?: string[];
}

/**
 * A side-nav entry computed per caller rather than declared in the manifest.
 *
 * A plugin names a route in its manifest's `dynamicPages`; that route returns
 * these. They are how one view serves many named entries - a user's own
 * dashboards, or the one template their role maps to - which a static manifest
 * cannot express because it is written before anyone logs in.
 */
export interface PluginDynamicPage {
	name: string;
	icon?: string;
	/** The plugin view this entry opens. */
	view: string;
	/** Optional; distinguishes several entries sharing one view. */
	entityId?: string;
}

export interface PluginKpiNavigate {
	pluginId?: string;
	view?: string;
	/**
	 * On a KPI tile these are literal values. On a table tile a value may be a
	 * `${Field}` placeholder, substituted per row from that row's own data — so
	 * one declaration serves five rows that each open a different record.
	 */
	params?: Record<string, string>;
}

/** One column of a `type: "table"` tile. */
export interface PluginKpiColumn {
	/** A literal, or "{i18n>key}" into the PLUGIN's own bundle. */
	label: string;
	/** The field to read off each row. */
	key: string;
	align?: "Begin" | "End";
	format?: "text" | "number" | "date";
	/**
	 * Coded value -> label, e.g. {"overdue": "{i18n>statusOverdue}"}. For fields
	 * holding a token rather than something a reader should see. An unmapped
	 * value passes through unchanged, so a partial map is safe.
	 */
	map?: Record<string, string>;
}

/**
 * What a value MEANS, in presentation words rather than business words.
 *
 * The plugin says "negative"; core maps that to a colour. Core never learns
 * that "overdue" is bad — that knowledge stays in the manifest of the plugin
 * that owns the number.
 */
export type PluginKpiTone =
	| "informative"
	| "negative"
	| "critical"
	| "highlight"
	| "positive"
	| "neutral";

/** The pinned link at the bottom of a grid card. */
export interface PluginKpiFooter {
	/** May contain "{0}", replaced by the count. */
	label: string;
	/**
	 * Which field of the SAME response holds the total. Invoices, Quotations,
	 * Orders and Tickets all return `totalCount` beside their rows, so the
	 * count costs no extra request.
	 */
	countKey?: string;
	/** Defaults to the tile's own `navigate`. */
	navigate?: PluginKpiNavigate;
}

/**
 * One dashboard tile declared by a plugin.
 *
 * `type` is the discriminator, and it is ABSENT-SAFE: absent or "kpi" is the
 * numeric tile this interface originally described, so every manifest written
 * before table tiles existed behaves byte-identically. Every field added since
 * is optional for the same reason.
 */
export interface PluginKpi {
	label: string;
	url: string;
	icon?: string;
	valueKey?: string;
	navigate?: PluginKpiNavigate;

	// --- type: "table" only ---
	type?: "kpi" | "table";
	/**
	 * Where the row array sits in the response. Not decoration: the envelopes
	 * genuinely differ - Invoices, Orders, Quotations, PurchaseOrders and
	 * Tickets return {value: [...]}, Delivery returns {items: [...]}, and ""
	 * means the payload IS the array.
	 */
	rowsKey?: string;
	/** Rows to show. Default 5 - this is a dashboard, not a list page. */
	rowLimit?: number;
	/** Shown when the response held no rows. */
	emptyText?: string;
	columns?: PluginKpiColumn[];

	// --- placement and presentation, both kinds ---
	/**
	 * Which of the dashboard's two containers this belongs in: the strip of
	 * small figures across the top, or the grid of cards below.
	 *
	 * Defaults from `type`, so a manifest written before this existed keeps its
	 * numeric tiles in the strip and contributes nothing to the grid.
	 */
	slot?: "strip" | "grid";
	/**
	 * Sort key across ALL plugins, ascending. Default 1000, with the plugin's
	 * own order breaking ties — so a manifest that says nothing renders in
	 * exactly the sequence it does today.
	 */
	order?: number;
	/** What this value normally means. Default "neutral". */
	tone?: PluginKpiTone;
	/**
	 * What ZERO means. Default "neutral".
	 *
	 * ⚠️ Core clamps this: a zero can never be rendered negative or critical,
	 * whatever a manifest asks for. An absence of things is not an alarm.
	 */
	toneWhenZero?: PluginKpiTone;
	/** Strip card: the small line under the figure. */
	footnote?: string;
	/** Grid card: the small line beside the title. Defaults to the plugin name. */
	meta?: string;
	footer?: PluginKpiFooter;
}

export interface PluginInfo {
	id: string;
	/** Plain technical name from plugin.json — never translated. */
	name: string;
	/**
	 * Optional translatable label, typically an "{i18n>key}" reference into the
	 * plugin's own bundle. Resolve with PluginI18nService and fall back to `name`.
	 */
	displayName?: string;
	version: string;
	description: string;
	icon: string;
	namespace: string;
	enabled: boolean;
	installedAt: string;
	envVars: PluginEnvVar[];
	config: Record<string, string>;
	userVariables: PluginUserVariable[];
	roles: string[];
	permissions: string[];
	pages: PluginPage[];
	/**
	 * Route name serving this plugin's per-caller nav entries, or "" for none.
	 * See PluginDynamicPage.
	 */
	dynamicPages?: string;
	kpis: PluginKpi[];
}

export interface PluginConfig {
	envVars: PluginEnvVar[];
	config: Record<string, string>;
}

export interface ExposedRouteParam {
	name: string;
	type: string;
	required: boolean;
	description: string;
	/** For type "json": object field shape. */
	fields?: ExposedRouteParam[];
	/** For type "json": array element shape. */
	item?: { type?: string; fields?: ExposedRouteParam[] };
}

export interface ExposedRoute {
	pluginId: string;
	pluginName: string;
	method: string;
	path: string;
	description: string;
	params: ExposedRouteParam[];
}

const PluginService = {
	async getPlugins(): Promise<PluginInfo[]> {
		const response = await AuthService.authenticatedFetch("/api/plugins");
		if (!response.ok) {
			throw new Error("Failed to fetch plugins");
		}
		return response.json() as Promise<PluginInfo[]>;
	},

	async uploadPlugin(
		file: File,
		options: { update?: boolean } = {},
	): Promise<PluginInfo> {
		const formData = new FormData();
		formData.append("file", file);

		const url = options.update ? "/api/plugins?update=true" : "/api/plugins";
		const response = await AuthService.authenticatedFetch(url, {
			method: "POST",
			body: formData,
		});
		if (!response.ok) {
			const body = (await response.json().catch(() => ({
				message: "Upload failed",
			}))) as {
				message: string;
				code?: string;
				name?: string;
				currentVersion?: string;
				newVersion?: string;
			};
			const err = new Error(body.message) as Error & {
				status: number;
				code?: string;
				pluginName?: string;
				currentVersion?: string;
				newVersion?: string;
			};
			err.status = response.status;
			err.code = body.code;
			err.pluginName = body.name;
			err.currentVersion = body.currentVersion;
			err.newVersion = body.newVersion;
			throw err;
		}
		return response.json() as Promise<PluginInfo>;
	},

	/**
	 * The caller's own nav entries for one plugin.
	 *
	 * Never throws: a plugin whose route is missing, slow or broken must cost it
	 * its extra entries, not the whole side navigation.
	 */
	async getDynamicPages(id: string, route: string): Promise<PluginDynamicPage[]> {
		try {
			const response = await AuthService.authenticatedFetch(
				`/api/plugins/${encodeURIComponent(id)}/${encodeURIComponent(route)}`,
			);
			if (!response.ok) {
				return [];
			}
			const body: unknown = await response.json();
			const list = Array.isArray(body)
				? body
				: Array.isArray((body as { value?: unknown }).value)
					? ((body as { value: unknown[] }).value)
					: [];
			return list
				.filter(
					(entry): entry is PluginDynamicPage =>
						!!entry &&
						typeof entry === "object" &&
						typeof (entry as PluginDynamicPage).name === "string" &&
						typeof (entry as PluginDynamicPage).view === "string",
				)
				.map((entry) => ({
					...entry,
					// A plugin may hand back a numeric id; the nav key concatenates
					// it, so normalise here rather than at every call site.
					entityId:
						entry.entityId === undefined || entry.entityId === null
							? undefined
							: String(entry.entityId),
				}));
		} catch {
			return [];
		}
	},

	async enablePlugin(id: string): Promise<PluginInfo> {
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(id)}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ enabled: true }),
			},
		);
		if (!response.ok) {
			throw new Error("Failed to enable plugin");
		}
		return response.json() as Promise<PluginInfo>;
	},

	async disablePlugin(id: string): Promise<PluginInfo> {
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(id)}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ enabled: false }),
			},
		);
		if (!response.ok) {
			throw new Error("Failed to disable plugin");
		}
		return response.json() as Promise<PluginInfo>;
	},

	async removePlugin(id: string): Promise<void> {
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(id)}`,
			{
				method: "DELETE",
			},
		);
		if (!response.ok) {
			throw new Error("Failed to remove plugin");
		}
	},

	async getPluginConfig(id: string): Promise<PluginConfig> {
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(id)}/config`,
		);
		if (!response.ok) {
			throw new Error("Failed to fetch plugin config");
		}
		return response.json() as Promise<PluginConfig>;
	},

	async updatePluginConfig(
		id: string,
		config: Record<string, string>,
	): Promise<PluginConfig> {
		const response = await AuthService.authenticatedFetch(
			`/api/plugins/${encodeURIComponent(id)}/config`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(config),
			},
		);
		if (!response.ok) {
			throw new Error("Failed to update plugin config");
		}
		return response.json() as Promise<PluginConfig>;
	},

	async getExposedRoutes(): Promise<ExposedRoute[]> {
		const response = await AuthService.authenticatedFetch(
			"/api/plugins/exposed-routes",
		);
		if (!response.ok) {
			throw new Error("Failed to fetch exposed routes");
		}
		const body = (await response.json()) as { routes?: ExposedRoute[] };
		return body.routes ?? [];
	},

	async reorderPlugins(order: string[]): Promise<void> {
		const response = await AuthService.authenticatedFetch(
			"/api/plugin-order",
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ order }),
			},
		);
		if (!response.ok) {
			throw new Error("Failed to reorder plugins");
		}
	},
};

export default PluginService;
